import type { ExecutorRunEvent } from "./executorRunTypes";
import {
  isExcelInternalTool,
  summarizeExcelSkillResult,
  summarizeExcelToolArgs,
} from "./agentTimelineMeta";
import { getCitationLabel } from "../shared/citations/colors";
import type { SkillReferenceSnapshot } from "../shared/skills/turnMetadata";

export type ExcelAgentToolStatus = "calling" | "completed" | "failed";

export interface ExcelResearchGroupProgress {
  completedRequests: number;
  totalRequests: number;
}

export interface ExcelWorkloadProgress {
  kind: "requests" | "closed_rows";
  completed: number;
  total: number;
  usable?: number;
  failed?: number;
}

export interface ExcelAgentToolView {
  callId: string;
  tool: string;
  status: ExcelAgentToolStatus;
  argsPreview?: string;
  elapsedMs?: number;
  resultPreview?: string;
  result?: unknown;
  /** Display-safe formulas retained after raw tool results are discarded. */
  formulaResults?: string[];
  /** Opaque key for a task-pane-memory-only visual snapshot preview. */
  snapshotPreviewId?: string;
  internal?: boolean;
  sub?: ExcelAgentSubAgentView;
  researchGroupProgress?: ExcelResearchGroupProgress;
  workloadProgress?: ExcelWorkloadProgress;
}

export interface ExcelAgentSubToolView {
  subCallId: string;
  tool: string;
  status: ExcelAgentToolStatus;
  description?: string;
  sectionTitle?: string;
}

export interface ExcelAgentSubHopView {
  subHop: number;
  thinkingText: string;
  tools: ExcelAgentSubToolView[];
}

export interface ExcelAgentSubAgentView {
  hops: ExcelAgentSubHopView[];
  liveAnswer: string;
  finalAnswer?: string;
  citations: unknown[];
  status: "running" | "completed" | "failed";
  statusText?: string;
  delegations?: ExcelAgentDelegationView[];
}

export interface ExcelAgentDelegationView {
  delegationId: string;
  title: string;
  query: string;
  childSessionId?: string;
  queryId?: string;
  hops: ExcelAgentSubHopView[];
  liveAnswer: string;
  finalAnswer?: string;
  citations: unknown[];
  status: "running" | "completed" | "failed";
  statusText?: string;
}

export type ExcelAgentTimelineBlock =
  | {
      kind: "trace";
      thinkingText: string;
      toolCallIds: string[];
    }
  | {
      kind: "assistant";
      text: string;
    };

export interface ExcelAgentHopView {
  hop: number;
  thinkingText: string;
  tools: ExcelAgentToolView[];
  /** Ordered native-stream presentation blocks; absent on legacy persisted timelines. */
  blocks?: ExcelAgentTimelineBlock[];
}

export interface ExcelAgentSource {
  id: string;
  title: string;
  source: string;
  url?: string;
  content?: string;
  company?: string;
  ticker?: string;
  date?: string;
  citedPassageCount?: number;
  citedSnippets?: string[];
}

export interface ExcelAgentTimelineState {
  hops: ExcelAgentHopView[];
  status?: string;
  /**
   * User-authored Skills applied to this run, as display references only.
   *
   * This state is persisted with the message, so it holds `{id, name, slug,
   * origin}` and never guidance. Distinct from the built-in modeling skills
   * loaded by the `get_skill` tool, which appear as ordinary tool rows.
   */
  activeUserSkills?: readonly SkillReferenceSnapshot[];
}

/**
 * Records which user Skills a run is operating under. Applied once, before the
 * first model turn, so the banner is present for the whole run.
 */
export function withActiveUserSkills(
  state: ExcelAgentTimelineState,
  skills: readonly SkillReferenceSnapshot[]
): ExcelAgentTimelineState {
  if (skills.length === 0) return state;
  return { ...state, activeUserSkills: skills };
}

/**
 * True when the timeline has something to show. Active Skills count on their
 * own: they are recorded before the first model hop, and the run's guidance is
 * exactly what the user needs to see while the first hop is still pending.
 */
export function hasExcelAgentTimelineContent(state: ExcelAgentTimelineState | undefined): boolean {
  if (!state) return false;
  return state.hops.length > 0 || (state.activeUserSkills?.length ?? 0) > 0;
}

/** Drops internal control tools, which are never shown to users. */
export function visibleExcelAgentTools(tools: ExcelAgentToolView[]): ExcelAgentToolView[] {
  return tools.filter((tool) => !tool.internal);
}

/**
 * Hops the timeline actually draws. A hop with neither reasoning, a
 * user-visible tool, nor streamed assistant text folds away, so raw
 * `hops.length` reports progress a reader cannot see.
 */
export function visibleExcelAgentHops(
  state: ExcelAgentTimelineState | undefined
): ExcelAgentHopView[] {
  // Hops rehydrated from a persisted conversation can arrive partially shaped.
  return (state?.hops ?? []).filter(
    (hop) =>
      (hop.thinkingText ?? "").trim().length > 0 ||
      visibleExcelAgentTools(hop.tools ?? []).length > 0 ||
      (hop.blocks ?? []).some((block) => block.kind === "assistant" && block.text.trim().length > 0)
  );
}

/**
 * Creates an empty timeline state for a new Excel agent run.
 */
export function emptyExcelAgentTimeline(): ExcelAgentTimelineState {
  return { hops: [] };
}

/**
 * Deep-clones the timeline so reducer updates can preserve React state
 * immutability.
 */
function cloneTimeline(state: ExcelAgentTimelineState): ExcelAgentTimelineState {
  return {
    status: state.status,
    activeUserSkills: state.activeUserSkills,
    hops: state.hops.map((hop) => ({
      ...hop,
      blocks: hop.blocks?.map((block) =>
        block.kind === "trace" ? { ...block, toolCallIds: [...block.toolCallIds] } : { ...block }
      ),
      tools: hop.tools.map((tool) => ({
        ...tool,
        formulaResults: tool.formulaResults ? [...tool.formulaResults] : undefined,
        researchGroupProgress: tool.researchGroupProgress
          ? { ...tool.researchGroupProgress }
          : undefined,
        workloadProgress: tool.workloadProgress ? { ...tool.workloadProgress } : undefined,
        sub: tool.sub
          ? {
              ...tool.sub,
              hops: tool.sub.hops.map((subHop) => ({
                ...subHop,
                tools: subHop.tools.map((subTool) => ({ ...subTool })),
              })),
              citations: [...tool.sub.citations],
              delegations: tool.sub.delegations?.map((delegation) => ({
                ...delegation,
                hops: delegation.hops.map((subHop) => ({
                  ...subHop,
                  tools: subHop.tools.map((subTool) => ({ ...subTool })),
                })),
                citations: [...delegation.citations],
              })),
            }
          : undefined,
      })),
    })),
  };
}

/**
 * Finds or creates the top-level hop for an executor iteration.
 */
function ensureHop(state: ExcelAgentTimelineState, hopNumber: number): ExcelAgentHopView {
  let hop = state.hops.find((candidate) => candidate.hop === hopNumber);
  if (!hop) {
    hop = { hop: hopNumber, thinkingText: "", tools: [], blocks: [] };
    state.hops.push(hop);
    state.hops.sort((a, b) => a.hop - b.hop);
  }
  return hop;
}

function appendReasoningBlock(hop: ExcelAgentHopView, text: string): void {
  const blocks = (hop.blocks ??= []);
  const last = blocks.at(-1);
  if (last?.kind === "trace") {
    last.thinkingText += text;
    return;
  }
  blocks.push({ kind: "trace", thinkingText: text, toolCallIds: [] });
}

function appendAssistantBlock(hop: ExcelAgentHopView, text: string): void {
  const blocks = (hop.blocks ??= []);
  const last = blocks.at(-1);
  if (last?.kind === "assistant") {
    last.text += text;
    return;
  }
  blocks.push({ kind: "assistant", text });
}

function attachToolToTraceBlock(hop: ExcelAgentHopView, callId: string): void {
  const blocks = (hop.blocks ??= []);
  if (blocks.some((block) => block.kind === "trace" && block.toolCallIds.includes(callId))) {
    return;
  }
  const last = blocks.at(-1);
  if (last?.kind === "trace") {
    last.toolCallIds.push(callId);
    return;
  }
  blocks.push({ kind: "trace", thinkingText: "", toolCallIds: [callId] });
}

/**
 * Parses a JSON tool result when possible, otherwise keeps the raw string.
 */
function parseResult(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

/**
 * Extract only returned formula text for the timeline disclosure.
 *
 * The full tool result remains transient and is removed by
 * {@link capExcelAgentTimeline}; this allowlisted projection is safe to retain
 * in live and persisted activity.
 */
function extractFormulaResults(value: unknown): string[] {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  const record = value as Record<string, unknown>;
  const entries = Array.isArray(record.results) ? record.results : [record];
  return entries.flatMap((entry) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return [];
    const formulaRecord = entry as Record<string, unknown>;
    const formula =
      typeof formulaRecord.formula === "string" && formulaRecord.formula.trim()
        ? formulaRecord.formula.trim()
        : typeof formulaRecord.formula_template === "string" &&
            formulaRecord.formula_template.trim()
          ? formulaRecord.formula_template.trim()
          : undefined;
    return formula ? [formula] : [];
  });
}

/**
 * Read an opaque snapshot-preview identifier from display-only metadata.
 *
 * @param value - Unknown metadata emitted with a completed tool event.
 * @returns Trimmed preview identifier, or `undefined` when none is present.
 */
function extractSnapshotPreviewId(value: unknown): string | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const record = value as {
    previewId?: unknown;
    snapshotPreviewId?: unknown;
  };
  const previewId = record.snapshotPreviewId ?? record.previewId;
  return typeof previewId === "string" && previewId.trim()
    ? previewId.trim()
    : undefined;
}

/** Summarize one `set_citations` result for its timeline pill. */
function summarizeCitationApplication(
  result: unknown,
): { preview: string; hasAppliedCitations: boolean } | undefined {
  if (!result || typeof result !== "object" || Array.isArray(result)) return undefined;
  const record = result as Record<string, unknown>;
  const citedCount = Number(record.citedCount);
  const failedCount = Number(record.failedCount);
  if (
    !Number.isInteger(citedCount) ||
    citedCount < 0 ||
    !Number.isInteger(failedCount) ||
    failedCount < 0
  ) {
    return undefined;
  }
  return {
    preview:
      `${citedCount} citation${citedCount === 1 ? "" : "s"} applied` +
      (failedCount > 0 ? ` · ${failedCount} skipped` : ""),
    hasAppliedCitations: citedCount > 0 && record.applied !== false,
  };
}

function isRunPlanSequencingSkip(result: unknown): boolean {
  return (
    !!result &&
    typeof result === "object" &&
    !Array.isArray(result) &&
    (result as { requiresRunPlan?: unknown }).requiresRunPlan === true &&
    (result as { skipped?: unknown }).skipped === true
  );
}

/**
 * Finds a top-level tool call by call id across all hops.
 */
function findTool(state: ExcelAgentTimelineState, callId: string): ExcelAgentToolView | undefined {
  for (const hop of state.hops) {
    const tool = hop.tools.find((candidate) => candidate.callId === callId);
    if (tool) return tool;
  }
  return undefined;
}

/**
 * Ensures a nested research timeline exists on a research tool call.
 */
function ensureSub(tool: ExcelAgentToolView): ExcelAgentSubAgentView {
  if (!tool.sub) {
    tool.sub = { hops: [], liveAnswer: "", citations: [], status: "running" };
  }
  return tool.sub;
}

/**
 * Finds or creates a nested research hop inside a parent research tool.
 */
function ensureSubHop(sub: ExcelAgentSubAgentView, subHopNumber: number): ExcelAgentSubHopView {
  let hop = sub.hops.find((candidate) => candidate.subHop === subHopNumber);
  if (!hop) {
    hop = { subHop: subHopNumber, thinkingText: "", tools: [] };
    sub.hops.push(hop);
    sub.hops.sort((a, b) => a.subHop - b.subHop);
  }
  return hop;
}

function ensureDelegation(
  sub: ExcelAgentSubAgentView,
  delegationId: string,
  title = "Delegated research",
  query = ""
): ExcelAgentDelegationView {
  const delegations = (sub.delegations ??= []);
  let delegation = delegations.find((candidate) => candidate.delegationId === delegationId);
  if (!delegation) {
    delegation = {
      delegationId,
      title,
      query,
      hops: [],
      liveAnswer: "",
      citations: [],
      status: "running",
    };
    delegations.push(delegation);
  }
  return delegation;
}

function ensureDelegationHop(
  delegation: ExcelAgentDelegationView,
  subHopNumber: number
): ExcelAgentSubHopView {
  let hop = delegation.hops.find((candidate) => candidate.subHop === subHopNumber);
  if (!hop) {
    hop = { subHop: subHopNumber, thinkingText: "", tools: [] };
    delegation.hops.push(hop);
    delegation.hops.sort((a, b) => a.subHop - b.subHop);
  }
  return hop;
}

function upsertSubTool(
  subHop: ExcelAgentSubHopView,
  event: {
    toolName: string;
    status: ExcelAgentToolStatus;
    callId?: string;
    hopIndex: number;
    description?: string;
    sectionTitle?: string;
  },
  idPrefix = ""
): void {
  const rawCallId = event.callId ?? `${event.toolName}-${event.hopIndex}`;
  const subCallId = idPrefix ? `${idPrefix}:${rawCallId}` : rawCallId;
  const existing = subHop.tools.find((candidate) => candidate.subCallId === subCallId);
  if (existing) {
    existing.status = event.status;
    existing.description = event.description ?? existing.description;
    existing.sectionTitle = event.sectionTitle ?? existing.sectionTitle;
    return;
  }
  subHop.tools.push({
    subCallId,
    tool: event.toolName,
    status: event.status,
    description: event.description,
    sectionTitle: event.sectionTitle,
  });
}

/**
 * Applies one executor event to the timeline state, including nested research
 * stream events from the Finster/FactSet tool.
 */
export function reduceExcelAgentEvent(
  previous: ExcelAgentTimelineState,
  event: ExecutorRunEvent
): ExcelAgentTimelineState {
  const state = cloneTimeline(previous);
  switch (event.type) {
    case "iteration_start":
      ensureHop(state, event.iteration);
      break;
    case "reasoning_delta": {
      const hop = ensureHop(state, event.iteration);
      hop.thinkingText += event.text;
      appendReasoningBlock(hop, event.text);
      break;
    }
    case "assistant_text_delta": {
      const hop = ensureHop(state, event.iteration);
      appendAssistantBlock(hop, event.text);
      break;
    }
    case "assistant_text_reset": {
      const hop = ensureHop(state, event.iteration);
      hop.blocks = hop.blocks?.filter((block) => block.kind !== "assistant");
      break;
    }
    case "tool_call_started": {
      const hop = ensureHop(state, event.iteration);
      const existing = hop.tools.find((tool) => tool.callId === event.callId);
      const internal = isExcelInternalTool(event.name);
      if (existing) {
        existing.tool = event.name;
        existing.status = "calling";
        existing.argsPreview =
          summarizeExcelToolArgs(event.name, event.args) ?? existing.argsPreview;
        existing.internal = internal;
      } else {
        hop.tools.push({
          callId: event.callId,
          tool: event.name,
          status: "calling",
          argsPreview: summarizeExcelToolArgs(event.name, event.args),
          internal,
        });
      }
      attachToolToTraceBlock(hop, event.callId);
      break;
    }
    case "tool_call_completed": {
      const tool = findTool(state, event.callId);
      if (tool) {
        const parsedResult = parseResult(event.result);
        if (isRunPlanSequencingSkip(parsedResult)) {
          for (const hop of state.hops) {
            const index = hop.tools.findIndex((candidate) => candidate.callId === event.callId);
            if (index !== -1) {
              hop.tools.splice(index, 1);
              break;
            }
          }
          break;
        }
        const citationApplication =
          tool.tool === "set_citations"
            ? summarizeCitationApplication(parsedResult)
            : undefined;
        tool.status = citationApplication?.hasAppliedCitations
          ? "completed"
          : event.status === "error"
            ? "failed"
            : "completed";
        tool.elapsedMs = event.elapsedMs;
        tool.resultPreview = event.resultPreview;
        tool.result = parsedResult;
        if (citationApplication) {
          tool.argsPreview = citationApplication.preview;
        }
        if (tool.tool === "generate_formula") {
          tool.formulaResults = extractFormulaResults(parsedResult);
        }
        if (tool.tool === "get_snapshot") {
          tool.snapshotPreviewId =
            extractSnapshotPreviewId(event.displayMetadata) ??
            extractSnapshotPreviewId(parsedResult);
        }
        if (tool.tool === "get_skill" && !tool.argsPreview) {
          tool.argsPreview = summarizeExcelSkillResult(tool.result);
        }
      }
      break;
    }
    case "research_sub_event": {
      const tool = findTool(state, event.callId);
      if (!tool) break;
      const sub = ensureSub(tool);
      switch (event.event.type) {
        case "reasoning": {
          ensureSubHop(sub, event.event.hopIndex).thinkingText += event.event.chunk;
          break;
        }
        case "tool": {
          const subHop = ensureSubHop(sub, event.event.hopIndex);
          upsertSubTool(subHop, event.event);
          if (event.event.status === "failed") sub.status = "failed";
          break;
        }
        case "answerDelta":
          sub.liveAnswer += event.event.chunk;
          break;
        case "final":
          sub.finalAnswer = event.event.answer;
          sub.citations = event.event.citations ?? [];
          sub.status = "completed";
          break;
        case "status":
          sub.statusText = event.event.status;
          break;
        case "session":
          break;
        case "delegationStarted": {
          const delegation = ensureDelegation(
            sub,
            event.event.delegationId,
            event.event.title,
            event.event.query
          );
          if (event.event.title) delegation.title = event.event.title;
          if (event.event.query) delegation.query = event.event.query;
          break;
        }
        case "delegationSession": {
          const delegation = ensureDelegation(
            sub,
            event.event.delegationId,
            undefined,
            event.event.query
          );
          delegation.childSessionId = event.event.childSessionId;
          break;
        }
        case "delegationStatus": {
          const delegation = ensureDelegation(sub, event.event.delegationId);
          delegation.statusText = event.event.status;
          delegation.childSessionId = event.event.childSessionId ?? delegation.childSessionId;
          break;
        }
        case "delegationReasoning": {
          const delegation = ensureDelegation(sub, event.event.delegationId);
          delegation.childSessionId = event.event.childSessionId ?? delegation.childSessionId;
          ensureDelegationHop(delegation, event.event.hopIndex).thinkingText += event.event.chunk;
          break;
        }
        case "delegationTool": {
          const delegation = ensureDelegation(sub, event.event.delegationId);
          delegation.childSessionId = event.event.childSessionId ?? delegation.childSessionId;
          const delegationHop = ensureDelegationHop(delegation, event.event.hopIndex);
          upsertSubTool(
            delegationHop,
            event.event,
            delegation.childSessionId ?? delegation.delegationId
          );
          break;
        }
        case "delegationAnswerDelta": {
          const delegation = ensureDelegation(sub, event.event.delegationId);
          delegation.childSessionId = event.event.childSessionId ?? delegation.childSessionId;
          delegation.liveAnswer += event.event.chunk;
          break;
        }
        case "delegationFinal": {
          const delegation = ensureDelegation(sub, event.event.delegationId);
          delegation.childSessionId = event.event.childSessionId ?? delegation.childSessionId;
          delegation.queryId = event.event.queryId ?? delegation.queryId;
          delegation.finalAnswer = event.event.answer;
          delegation.citations = event.event.citations ?? [];
          delegation.status = "completed";
          break;
        }
        case "delegationCompleted": {
          const delegation = ensureDelegation(sub, event.event.delegationId);
          delegation.status = delegation.finalAnswer ? "completed" : event.event.status;
          delegation.statusText = event.event.message ?? delegation.statusText;
          break;
        }
      }
      break;
    }
    case "research_group_progress": {
      const hop = ensureHop(state, event.iteration);
      const progress: ExcelResearchGroupProgress = {
        completedRequests: event.completedRequests,
        totalRequests: event.totalRequests,
      };
      const existing = hop.tools.find((tool) => tool.callId === event.groupId);
      if (existing) {
        existing.status = event.completedRequests >= event.totalRequests ? "completed" : "calling";
        existing.researchGroupProgress = progress;
      } else {
        hop.tools.push({
          callId: event.groupId,
          tool: event.name,
          status: event.completedRequests >= event.totalRequests ? "completed" : "calling",
          researchGroupProgress: progress,
        });
      }
      break;
    }
    case "workload_progress": {
      const hop = ensureHop(state, event.iteration);
      const progress: ExcelWorkloadProgress = {
        kind: event.kind,
        completed: event.completed,
        total: event.total,
        ...(event.usable !== undefined ? { usable: event.usable } : {}),
        ...(event.failed !== undefined ? { failed: event.failed } : {}),
      };
      const existing =
        findTool(state, event.callId) ?? hop.tools.find((tool) => tool.callId === event.callId);
      if (existing) {
        existing.workloadProgress = progress;
      } else {
        hop.tools.push({
          callId: event.callId,
          tool: event.name,
          status: "calling",
          workloadProgress: progress,
        });
      }
      break;
    }
    case "status":
      state.status = event.message;
      break;
    case "plan_state_changed":
      break;
  }
  return state;
}

/**
 * Close any activity that was still running when a run ended unexpectedly.
 *
 * Persisted timelines are rendered without a live stream, so retaining
 * `calling`/`running` would leave permanent spinners on cancelled or failed
 * runs.
 */
export function finalizeFailedExcelAgentTimeline(
  previous: ExcelAgentTimelineState,
): ExcelAgentTimelineState {
  const state = cloneTimeline(previous);
  for (const hop of state.hops) {
    for (const tool of hop.tools) {
      if (tool.status === "calling") tool.status = "failed";
      if (!tool.sub) continue;
      if (tool.sub.status === "running") tool.sub.status = "failed";
      for (const subHop of tool.sub.hops) {
        for (const subTool of subHop.tools) {
          if (subTool.status === "calling") subTool.status = "failed";
        }
      }
      for (const delegation of tool.sub.delegations ?? []) {
        if (delegation.status === "running") delegation.status = "failed";
        for (const subHop of delegation.hops) {
          for (const subTool of subHop.tools) {
            if (subTool.status === "calling") subTool.status = "failed";
          }
        }
      }
    }
  }
  return state;
}

/**
 * Produces a bounded, display-safe copy of the timeline for persistence or UI
 * rendering by trimming large strings and hiding internal tool results.
 */
export function capExcelAgentTimeline(
  state: ExcelAgentTimelineState,
  opts?: { maxHops?: number; maxTextChars?: number }
): ExcelAgentTimelineState {
  const maxHops = opts?.maxHops ?? 24;
  const maxTextChars = opts?.maxTextChars ?? 2400;
  const trim = (text: string | undefined) =>
    text && text.length > maxTextChars ? `${text.slice(0, maxTextChars)}...` : text;
  return {
    status: trim(state.status),
    activeUserSkills: state.activeUserSkills,
    hops: state.hops
      .map((hop) => {
        const tools = hop.tools
          .filter((tool) => !tool.internal)
          .map((tool) => ({
            ...tool,
            resultPreview: trim(tool.resultPreview),
            result: undefined,
            formulaResults: tool.formulaResults
              ?.slice(0, 20)
              .map((formula) => trim(formula) ?? ""),
            sub: tool.sub
              ? {
                  ...tool.sub,
                  liveAnswer: trim(tool.sub.liveAnswer) ?? "",
                  finalAnswer: trim(tool.sub.finalAnswer),
                  hops: tool.sub.hops.map((subHop) => ({
                    subHop: subHop.subHop,
                    thinkingText: trim(subHop.thinkingText) ?? "",
                    tools: subHop.tools.map((subTool) => ({ ...subTool })),
                  })),
                  citations: tool.sub.citations.slice(0, 20),
                  delegations: tool.sub.delegations?.map((delegation) => ({
                    ...delegation,
                    title: trim(delegation.title) ?? "",
                    query: trim(delegation.query) ?? "",
                    liveAnswer: trim(delegation.liveAnswer) ?? "",
                    finalAnswer: trim(delegation.finalAnswer),
                    statusText: trim(delegation.statusText),
                    hops: delegation.hops.map((subHop) => ({
                      subHop: subHop.subHop,
                      thinkingText: trim(subHop.thinkingText) ?? "",
                      tools: subHop.tools.map((subTool) => ({ ...subTool })),
                    })),
                    citations: delegation.citations.slice(0, 20),
                  })),
                }
              : undefined,
          }));
        const visibleToolIds = new Set(tools.map((tool) => tool.callId));
        const blocks = hop.blocks
          ?.map(
            (block): ExcelAgentTimelineBlock =>
              block.kind === "assistant"
                ? {
                    kind: "assistant",
                    text: trim(block.text) ?? "",
                  }
                : {
                    kind: "trace",
                    thinkingText: trim(block.thinkingText) ?? "",
                    toolCallIds: block.toolCallIds.filter((callId) => visibleToolIds.has(callId)),
                  }
          )
          .filter((block) =>
            block.kind === "assistant"
              ? Boolean(block.text.trim())
              : Boolean(block.thinkingText.trim() || block.toolCallIds.length)
          );
        return {
          hop: hop.hop,
          thinkingText: trim(hop.thinkingText) ?? "",
          tools,
          ...(blocks ? { blocks } : {}),
        };
      })
      .filter(
        (hop) => hop.thinkingText.trim() || hop.tools.length > 0 || (hop.blocks?.length ?? 0) > 0
      )
      .slice(-maxHops),
  };
}

/**
 * Removes a final streamed assistant block when the persisted chat answer will
 * render the same text immediately below the timeline.
 */
export function withoutDuplicateTerminalAssistantText(
  state: ExcelAgentTimelineState,
  terminalAnswer: string
): ExcelAgentTimelineState {
  const next = cloneTimeline(state);
  const normalizedAnswer = terminalAnswer.trim();
  if (!normalizedAnswer) return next;
  for (let hopIndex = next.hops.length - 1; hopIndex >= 0; hopIndex -= 1) {
    const blocks = next.hops[hopIndex].blocks;
    if (!blocks?.length) continue;
    for (let blockIndex = blocks.length - 1; blockIndex >= 0; blockIndex -= 1) {
      const block = blocks[blockIndex];
      if (block.kind !== "assistant") continue;
      if (block.text.trim() === normalizedAnswer) {
        blocks.splice(blockIndex, 1);
      }
      next.hops = next.hops.filter(
        (hop) => hop.thinkingText.trim() || hop.tools.length > 0 || (hop.blocks?.length ?? 0) > 0
      );
      return next;
    }
  }
  return next;
}

/**
 * Projects raw provider citations into user-visible source cards. Research
 * citations reach the UI on the tool-completed event rather than in the tool
 * output, so they never re-enter the model's context.
 */
export function extractSourcesFromCitations(citations: unknown): ExcelAgentSource[] {
  if (!Array.isArray(citations)) return [];
  return citations.flatMap((raw, index) => normalizeCitation(raw, index));
}

/**
 * Appends newly retrieved sources while preserving the first instance of each
 * source key.
 */
export function mergeExcelAgentSources(
  existing: readonly ExcelAgentSource[] | undefined,
  next: readonly ExcelAgentSource[]
): ExcelAgentSource[] {
  const merged: ExcelAgentSource[] = [];
  const seen = new Set<string>();
  for (const source of [...(existing ?? []), ...next]) {
    const key = sourceKey(source);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(source);
  }
  return merged;
}

/**
 * Converts one raw citation payload into one or more source cards.
 */
function normalizeCitation(raw: unknown, index: number): ExcelAgentSource[] {
  if (!raw || typeof raw !== "object") return [];
  const citation = raw as Record<string, unknown>;
  const data = citation.citationData ?? citation.citation_data;
  const metadata = objectFrom(citation.metadata);
  const client = stringFrom(citation.client);
  const label = getCitationLabel(stringFrom(metadata?.dataSource), client);

  if (typeof data === "string") {
    const ids = extractCitationIds(data);
    const title =
      stringFrom(metadata?.documentName) ??
      stringFrom(metadata?.companyName) ??
      (label !== "Other" ? label : "Data source");
    return [
      {
        id: `table:${client ?? "unknown"}:${hashString(data)}`,
        title,
        source: label,
        content: data,
        company: stringFrom(metadata?.companyName),
        ticker: stringFrom(metadata?.ticker),
        date: stringFrom(metadata?.datePublished),
        citedPassageCount: ids.length || undefined,
      },
    ];
  }

  if (isDictionaryData(data)) {
    return Object.entries(data).flatMap(([key, value], entryIndex) => {
      if (!value || typeof value !== "object") return [];
      const link = value as Record<string, unknown>;
      const linkMetadata = objectFrom(link.metadata);
      const source = getCitationLabel(
        stringFrom(linkMetadata?.dataSource) ?? stringFrom(metadata?.dataSource),
        client
      );
      const title =
        stringFrom(linkMetadata?.documentName) ??
        cleanText(stringFrom(link.text)) ??
        stringFrom(link.link);
      if (!title) return [];
      const citedIds = [
        ...extractCitationIds(stringFrom(link.text) ?? ""),
        ...extractCitationIds(key),
      ];
      const snippet = cleanText(stringFrom(link.text));
      const citedSnippets = snippet ? [snippet] : undefined;
      const citedPassageCount = (citedSnippets?.length ?? citedIds.length) || undefined;
      const sourceId = stringFrom(link.link) ?? (key || `${index}-${entryIndex}`);
      return [
        {
          id: `document:${client ?? "unknown"}:${sourceId}`,
          title,
          source,
          url: stringFrom(link.link) ?? stringFrom(link.originalPdfUrl),
          content: stringFrom(link.text),
          company: stringFrom(linkMetadata?.companyName),
          ticker: stringFrom(linkMetadata?.ticker),
          date: stringFrom(linkMetadata?.datePublished),
          citedPassageCount,
          citedSnippets,
        },
      ];
    });
  }

  return [];
}

/**
 * Returns a trimmed string value when present.
 */
function stringFrom(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

/**
 * Strips simple HTML tags and collapses whitespace for preview text.
 */
function cleanText(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const cleaned = value
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned || undefined;
}

/**
 * Narrows an unknown value to a plain object record.
 */
function objectFrom(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

/**
 * Checks whether citation data is the dictionary-link shape.
 */
function isDictionaryData(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

/**
 * Extracts citation ids embedded in markdown/html attributes.
 */
function extractCitationIds(text: string): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  const patterns = [
    /\bid\s*=\s*["']([0-9a-fA-F-]{8,})["']/g,
    /\bdata-citation-id\s*=\s*["']([0-9a-fA-F-]{8,})["']/g,
  ];
  for (const pattern of patterns) {
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(text)) !== null) {
      const id = match[1];
      if (!seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    }
  }
  return ids;
}

/**
 * Builds the de-duplication key used when merging source lists.
 */
function sourceKey(source: ExcelAgentSource): string {
  return (
    source.id || `${source.source}:${source.title}:${source.ticker ?? ""}:${source.date ?? ""}`
  );
}

/**
 * Produces a short stable id suffix for markdown citation blobs.
 */
function hashString(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = ((hash << 5) - hash + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(36);
}
